/**
 * @file TabTexts.jsx
 * @description TabTexts component for displaying and managing tab texts.
 *              Conversations and the rows inside them are ordered newest-first,
 *              and the platform-inserted "Hi" opener is hidden.
 * @component TabTexts
 * @exports TabTexts - Main component for displaying and managing tab texts.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ProgressOverlay from '../../../common/components/ProgressOverlay.jsx';
import {
  getcustomertexts,
} from "../../../services/customerinfoModule/historyServices";

import { fetchCustomerTextChart } from "../../../services/dashboardModule/dashboardServices";
import { getConversationCompliance, getconversationcomplianceForR413 } from '../../../services/customerinfoModule/historyServices';
import CustomerComplianceModal from '../../customerModule/customers/CustomerComplianceModal.jsx';
import CustComplianceSummaryModal from '../../customerModule/customers/CustComplianceSummaryModal.jsx';
import { IconButton } from '@mui/material';
import CustomerDashTextChart from "../../dashboard/customersDash/charts/CustomerDashTextChart.jsx";
import { useRegisterCsvExport } from '../../customerModule/customers/CsvExportContext.jsx';
import { TEXTS_COLUMNS } from '../../customerModule/customers/customerExportConfig.js';

const timeMs = (val) => {
  if (!val) return 0;
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// The opening "Hi" is written by the platform, not typed by the customer.
// Matches the predicate /gethiresponses uses (userresponse = 'Hi' AND
// iniconv = 0); if iniconv isn't in the SELECT list we fall back to the text
// match alone.
const isInsertedHi = (item) => {
  if (!/^hi[.!]?$/i.test(String(item.userresponse || "").trim())) return false;
  const ini = item.iniconv ?? item.IniConv ?? item.Iniconv;
  return ini == null ? true : Number(ini) === 0;
};

const USER_COLOR = "#378ADD";
const CARA_COLOR = "#1D9E75";

// Split a stored row into its individual messages. Each row holds the
// customer's message and Cara's reply to it; the transcript shows them as
// separate lines so everything can be ordered on its own timestamp.
const toMessages = (item, idx) => {
  const out = [];
  if (item.cararesponse) {
    out.push({
      _idx: idx,
      time: item.cararesponsetime,
      who: "CARA",
      color: CARA_COLOR,
      message: item.cararesponse,
    });
  }
  if (item.userresponse && !isInsertedHi(item)) {
    out.push({
      _idx: idx,
      time: item.userresponsetime,
      who: "USER",
      color: USER_COLOR,
      message: item.userresponse,
    });
  }
  return out;
};

const CustomerTexts = ({ customerguid, openSendText, onCloseSendText, isDash }) => {
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  // error state kept for future use
  const [errorMsg] = useState("");
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceData, setComplianceData] = useState([]);
  const [complianceError, setComplianceError] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryError, setSummaryError] = useState("");
  const [chartData, setChartData] = useState([]);

  // Export stays the raw feed — the screen hides the inserted "Hi", the CSV
  // keeps the complete record.
  useRegisterCsvExport({
    name: "texts",
    columns: TEXTS_COLUMNS,
    rows: texts,
    enabled: !loading,
  });

  useEffect(() => {
    const fetchTexts = async () => {
      setLoading(true);
      try {
        const data = await getcustomertexts({ customerguid });
        setTexts(data || []);
      } catch (error) {
        console.error("Error fetching texts:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchChart = async () => {
      try {
        const data = await fetchCustomerTextChart(customerguid);
        setChartData(data || []);
      } catch (error) {
        console.error("Error fetching text chart:", error);
      }
    };

    if (customerguid) {
      fetchTexts();
      if (isDash) fetchChart();
    }
  }, [customerguid, isDash]);

  // MM/dd/yyyy hh:mm:ss AM/PM
  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, '0');
    const secs = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${mm}/${dd}/${yyyy} ${String(hours).padStart(2, '0')}:${mins}:${secs} ${ampm}`;
  };

  // Group by ConversationID15, flatten each conversation into a message
  // transcript ordered strictly newest-first, then order the conversations
  // themselves by most recent activity. Since Cara always answers after the
  // customer, descending order puts each Cara message directly above the
  // message it responds to.
  const conversations = useMemo(() => {
    const map = new Map();
    (texts || []).forEach((item, idx) => {
      const key = item.ConversationID15 ?? 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(...toMessages(item, idx));
    });

    return Array.from(map.entries())
      .map(([convId, messages]) => {
        messages.sort((a, b) => {
          const d = timeMs(b.time) - timeMs(a.time);
          if (d) return d;
          // Same timestamp to the second: Cara above the reply she answers.
          if (a.who !== b.who) return a.who === "CARA" ? -1 : 1;
          return b._idx - a._idx;
        });
        return {
          convId,
          messages,
          lastTime: messages.length ? timeMs(messages[0].time) : 0,
        };
      })
      .filter((c) => c.messages.length > 0)
      .sort((a, b) => b.lastTime - a.lastTime);
  }, [texts]);

  const openComplianceForConversation = async (convId) => {
    setComplianceError("");
    setComplianceData([]);
    setComplianceLoading(true);
    setComplianceOpen(true);
    try {
      const data = await getConversationCompliance({ conversationID: convId });
      setComplianceData(data || []);
    } catch (err) {
      console.error('Failed to fetch compliance', err);
      setComplianceError('Failed to load compliance details');
    } finally {
      setComplianceLoading(false);
    }
  };

  const openSummaryForConversation = async (convId) => {
    setSummaryError("");
    setSummaryData([]);
    setSummaryLoading(true);
    setSummaryOpen(true);
    try {
      const data = await getconversationcomplianceForR413({ conversationID: convId });
      setSummaryData(data || []);
    } catch (err) {
      console.error('Failed to fetch summary', err);
      setSummaryError('Failed to load summary details');
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {isDash && (
        <CustomerDashTextChart
          rows={chartData}
          title="Text Volume"
          label="Texts"
          isCurrency={false}
        />
      )}
      {loading && <ProgressOverlay />}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {conversations.length === 0 && !loading ? (
        <Alert severity="info">No texts found</Alert>
      ) : (
        conversations.map(({ convId, messages }) => (
          <Accordion key={convId} sx={{ mb: 1 }}>
            <AccordionSummary sx={{ display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }} expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ ml: 1, fontSize: '0.875rem', flex: 1 }}>{`Conversation Id - ${convId}`}</Typography>
              <Tooltip title="Complaince">
                <FactCheckOutlinedIcon sx={{ cursor: 'pointer', color: 'gray', mr: 1 }}  onClick={(e) => { e.stopPropagation(); openComplianceForConversation(convId); }} aria-label={`compliance-${convId}`} />
              </Tooltip>
              <Tooltip title="R413 Summary">
                <IconButton sx={{ p: 0, color: 'gray' }} onClick={(e) => { e.stopPropagation(); openSummaryForConversation(convId); }} aria-label={`summary-${convId}`}>
                  <DescriptionOutlinedIcon />
                </IconButton>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 200, whiteSpace: 'nowrap' }}><b>Date</b></TableCell>
                      <TableCell sx={{ width: 80 }}><b>Sender</b></TableCell>
                      <TableCell><b>Message</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messages.map((msg, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                          verticalAlign: 'top',
                          color: 'text.secondary',
                        }}>
                          {formatDate(msg.time)}
                        </TableCell>
                        <TableCell sx={{
                          verticalAlign: 'top',
                          color: msg.color,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          letterSpacing: '0.04em',
                        }}>
                          {msg.who}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                          {msg.message}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
      <CustomerComplianceModal
        open={complianceOpen}
        onClose={() => setComplianceOpen(false)}
        loading={complianceLoading}
        data={complianceData}
        error={complianceError}
      />
      <CustComplianceSummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        loading={summaryLoading}
        data={summaryData}
        error={summaryError}
      />
    </Box>
  );
};

export default CustomerTexts;